import { css, styled } from "goober";
import { useRef, useState } from "react";
import {
  desktopViewportFrameCss,
  useDesktopViewportScale,
} from "../../hooks/useDesktopViewport";
import { CircleMesh } from "../CircleMesh";
import { BrowserChrome } from "../BrowserChrome";
import { HtmlViewer, type HtmlViewerHandle } from "../HtmlViewer";
import useInspectMode, {
  type InspectOptions,
} from "../LiveBrowser/useInspectMode";
import { useInspectShortcut } from "../../hooks/useInspectShortcut";

const StyledHtml = styled("div")`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  width: 100%;
  height: 100%;
`;

const bleedCss = (bleed: number) => css`
  margin: -${bleed}px;
  width: calc(100% + ${bleed * 2}px);
  height: calc(100% + ${bleed * 2}px);
`;

const Background = styled("div")`
  position: absolute;
  inset: 0;
`;

const PreviewContainer = styled("div")`
  position: relative;
  z-index: 1;
  width: 80%;
  max-width: 600px;
  aspect-ratio: 4 / 3;
  border-radius: 12px;
  outline: 0.5px solid var(--border-alpha);
  box-shadow: var(--elevation-low);
  overflow: hidden;
`;

const frame = css`
  ${desktopViewportFrameCss}
  display: block;
  border: none;
  background: transparent;
`;

// The interactive variant is a plain browser-like viewport: the page fills the
// panel at its own width and scrolls natively, rather than being scaled down
// into the preview card.
const InteractiveContainer = styled("div")`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  background: white;
`;

const interactiveFrame = css`
  display: block;
  width: 100%;
  height: 100%;
  border: none;
  background: white;
`;

type Props = {
  output: string;
  fileName?: string;
  bleed?: number;
  baseRoute?: string;
  interactive?: boolean;
  inspection?: Omit<InspectOptions, "enabled">;
};

export function Html({
  output,
  fileName,
  bleed,
  baseRoute,
  interactive,
  inspection,
}: Props) {
  const previewContainerRef = useDesktopViewportScale<HTMLDivElement>();
  const viewerRef = useRef<HtmlViewerHandle>(null);
  const [inspectMode, setInspectMode] = useState(false);
  const canInspect = interactive && inspection?.onElementSelect != null;
  const inspect = useInspectMode({
    ...inspection,
    enabled: canInspect && inspectMode,
  });
  useInspectShortcut(() => {
    if (canInspect) setInspectMode((previous) => !previous);
  });

  if (interactive) {
    return (
      <InteractiveContainer className={bleed ? bleedCss(bleed) : undefined}>
        <BrowserChrome
          displayUrl={baseRoute ?? fileName ?? "Webpage preview"}
          onBack={() => viewerRef.current?.back()}
          onForward={() => viewerRef.current?.forward()}
          onReload={() => viewerRef.current?.refresh()}
          canBack
          canForward
          inspectMode={inspectMode}
          onInspectModeChange={canInspect ? setInspectMode : undefined}
          embedded
        >
          <HtmlViewer
            apiRef={viewerRef}
            html={output}
            title={fileName ?? "Webpage preview"}
            className={interactiveFrame}
            baseRoute={baseRoute}
            interactive
            inspectFeature={canInspect ? inspect.feature : undefined}
          />
        </BrowserChrome>
      </InteractiveContainer>
    );
  }

  return (
    <StyledHtml className={bleed ? bleedCss(bleed) : undefined}>
      <Background>
        <CircleMesh mutingIntensity={1} seed="html-preview" />
      </Background>
      <PreviewContainer ref={previewContainerRef}>
        <HtmlViewer html={output} className={frame} baseRoute={baseRoute} />
      </PreviewContainer>
    </StyledHtml>
  );
}
